import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, Clock, CheckCircle, XCircle, Loader2, ArrowLeft, Users, Building2, Zap, Crown, Plus, ExternalLink, Settings } from 'lucide-react';
import SidebarLayout from '@/components/SidebarLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'control', name: 'Control', icon: Building2,
    description: 'Operación organizada',
    monthly: 49, annual: 470,
    features: ['1 fraccionamiento', '3 usuarios', 'Dashboard financiero', 'Estado de cuenta', 'Soporte estándar'],
    trial: true,
  },
  {
    id: 'profesional', name: 'Profesional', icon: Zap, popular: true,
    description: 'Control financiero avanzado',
    monthly: 89, annual: 854,
    features: ['3 fraccionamientos', '5 usuarios', 'KPIs financieros', 'Notificaciones', 'Comisiones', 'Soporte prioritario'],
  },
  {
    id: 'enterprise', name: 'Enterprise', icon: Crown,
    description: 'Plataforma corporativa',
    monthly: 149, annual: 1430,
    features: ['Ilimitados', '10 usuarios', 'Auditoría completa', 'Branding', 'Onboarding', 'Soporte 24/7'],
  },
];

export default function Subscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [user, setUser] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [accountUsage, setAccountUsage] = useState(null);

  const sessionId = searchParams.get('session_id');
  const sessionType = searchParams.get('type');

  useEffect(() => { checkAuth(); }, []);

  useEffect(() => {
    if (sessionId && user) pollPaymentStatus(sessionId);
  }, [sessionId, user]);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (!res.ok) { navigate('/login'); return; }
      const userData = await res.json();
      setUser(userData);
      fetchSubscriptionStatus();
      fetchAccountUsage();
    } catch (error) { 
      console.error('Auth check failed:', error);
      navigate('/login'); 
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/status`, { credentials: 'include' });
      if (res.ok) setSubscriptionInfo(await res.json());
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally { setLoading(false); }
  };

  const fetchAccountUsage = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/team/account`, { credentials: 'include' });
      if (res.ok) setAccountUsage(await res.json());
    } catch (error) {
      console.error('Error fetching account usage:', error);
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 10) {
      toast.error('Tiempo de espera agotado. Verifica tu correo.');
      setProcessingPayment(false);
      return;
    }
    setProcessingPayment(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/checkout-status/${sid}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      if (data.payment_status === 'paid') {
        toast.success(sessionType === 'seat' ? '¡Asiento(s) agregado(s)!' : '¡Suscripción activada!');
        setProcessingPayment(false);
        window.history.replaceState({}, document.title, '/subscription');
        fetchSubscriptionStatus();
        fetchAccountUsage();
        return;
      }
      if (data.status === 'expired') { toast.error('Sesión expirada.'); setProcessingPayment(false); return; }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch { setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000); }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPayment(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin, plan: planId, billing_cycle: billingCycle === 'annual' ? 'yearly' : 'monthly' })
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Error'); }
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message || 'Error al procesar.');
      setProcessingPayment(false);
    }
  };

  const handleAddSeat = async () => {
    setProcessingPayment(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/team/add-seat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ quantity: 1, origin_url: window.location.origin })
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Error'); }
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.success(data.message || 'Asiento agregado'); fetchAccountUsage(); setProcessingPayment(false); }
    } catch (error) {
      toast.error(error.message || 'Error');
      setProcessingPayment(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('¿Cancelar suscripción? Mantendrás acceso hasta fin del período.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/cancel`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('Suscripción cancelada.'); fetchSubscriptionStatus(); }
      else throw new Error();
    } catch { toast.error('Error al cancelar.'); }
  };

  const handleOpenPortal = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/customer-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ return_url: window.location.href })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error');
      }
      const data = await res.json();
      window.location.href = data.portal_url;
    } catch (error) {
      toast.error(error.message || 'Error al abrir el portal');
    }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

  const currentPlan = subscriptionInfo?.plan || accountUsage?.plan || 'control';
  const isActive = subscriptionInfo?.status === 'active';
  const isTrial = subscriptionInfo?.status === 'trial';
  const canSubscribe = isTrial || subscriptionInfo?.status === 'expired' || subscriptionInfo?.status === 'cancelled';

  const SubscriptionContent = () => (
    <div className="p-6 max-w-5xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-zinc-400 hover:text-white">
        <ArrowLeft className="w-4 h-4 mr-2" /> Volver al Dashboard
      </Button>

      {/* Processing */}
      {processingPayment && (
        <Card className="bg-zinc-900 border-amber-500/50 mb-6">
          <CardContent className="pt-6"><div className="flex items-center justify-center gap-3"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /><span className="text-white">Procesando...</span></div></CardContent>
        </Card>
      )}

      {/* Current Status */}
      <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="subscription-status-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Tu cuenta</CardTitle>
            {isTrial && <Badge className="bg-blue-600 text-white">Trial</Badge>}
            {isActive && <Badge className="bg-green-600 text-white">Activo</Badge>}
            {subscriptionInfo?.status === 'expired' && <Badge className="bg-red-600 text-white">Expirado</Badge>}
            {subscriptionInfo?.status === 'cancelled' && <Badge className="bg-yellow-600 text-black">Cancelado</Badge>}
            {subscriptionInfo?.status === 'past_due' && <Badge className="bg-orange-600 text-white">Pago pendiente</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-800 rounded-lg p-3 text-center">
              <p className="text-xs text-zinc-500 mb-1">Plan</p>
              <p className="text-white font-bold capitalize">{currentPlan}</p>
            </div>
            <div className="bg-zinc-800 rounded-lg p-3 text-center">
              <p className="text-xs text-zinc-500 mb-1">Usuarios</p>
              <p className="text-white font-bold">{accountUsage?.usuarios_activos || 0}/{accountUsage?.max_usuarios_permitidos || '-'}</p>
            </div>
            <div className="bg-zinc-800 rounded-lg p-3 text-center">
              <p className="text-xs text-zinc-500 mb-1">Fraccionamientos</p>
              <p className="text-white font-bold">{accountUsage?.fraccionamientos_usados || 0}/{accountUsage?.max_fraccionamientos || '-'}</p>
            </div>
            <div className="bg-zinc-800 rounded-lg p-3 text-center">
              <p className="text-xs text-zinc-500 mb-1">{isTrial ? 'Días restantes' : 'Renovación'}</p>
              <p className="text-white font-bold">{isTrial ? subscriptionInfo?.days_remaining : subscriptionInfo?.renewal_date?.split('T')[0] || '-'}</p>
            </div>
          </div>

          {/* Add seat button */}
          {(isActive || isTrial) && (
            <div className="mt-4 flex items-center justify-between bg-zinc-800 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-500" />
                <span className="text-zinc-300 text-sm">Usuarios disponibles: {accountUsage?.usuarios_disponibles || 0}</span>
              </div>
              <Button size="sm" onClick={handleAddSeat} disabled={processingPayment} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="add-seat-btn">
                <Plus className="w-4 h-4 mr-1" /> Agregar usuario
              </Button>
            </div>
          )}

          {isActive && (
            <div className="mt-4 flex gap-2">
              <Button onClick={handleOpenPortal} variant="outline" className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="customer-portal-btn">
                <Settings className="w-4 h-4 mr-2" /> Gestionar Suscripción
              </Button>
              <Button onClick={handleCancel} variant="outline" className="flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10" data-testid="cancel-subscription-button">
                Cancelar Suscripción
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans - show only if user can subscribe or wants to upgrade */}
      {(canSubscribe || isActive) && (
        <>
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white mb-3">{isActive ? 'Cambiar Plan' : 'Elige tu Plan'}</h2>
            <div className="inline-flex bg-zinc-900 p-1 rounded-xl border border-zinc-800">
              <button onClick={() => setBillingCycle('monthly')} className={`px-5 py-2 rounded-lg font-medium transition text-sm ${billingCycle === 'monthly' ? 'bg-amber-500 text-black' : 'text-zinc-400'}`}>
                Mensual
              </button>
              <button onClick={() => setBillingCycle('annual')} className={`px-5 py-2 rounded-lg font-medium transition text-sm flex items-center gap-1 ${billingCycle === 'annual' ? 'bg-amber-500 text-black' : 'text-zinc-400'}`}>
                Anual <Badge className="bg-green-500 text-white text-xs">-20%</Badge>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              const price = billingCycle === 'annual' ? plan.annual : plan.monthly;
              const isCurrentPlan = currentPlan === plan.id && isActive;
              return (
                <Card key={plan.id} className={`bg-zinc-900 ${plan.popular ? 'border-amber-500 ring-1 ring-amber-500/30' : 'border-zinc-800'} ${isCurrentPlan ? 'opacity-60' : ''}`} data-testid={`plan-${plan.id}`}>
                  {plan.popular && (
                    <div className="text-center pt-2"><Badge className="bg-amber-500 text-black font-bold text-xs">RECOMENDADO</Badge></div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${plan.popular ? 'text-amber-500' : 'text-zinc-400'}`} />
                      <CardTitle className="text-white text-lg">{plan.name}</CardTitle>
                    </div>
                    <p className="text-zinc-500 text-xs">{plan.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-white">${price}</span>
                      <span className="text-zinc-500 text-sm ml-1">USD/{billingCycle === 'annual' ? 'año' : 'mes'}</span>
                      {billingCycle === 'annual' && <p className="text-green-500 text-xs">Ahorras ${(plan.monthly * 12) - plan.annual} USD</p>}
                      {plan.trial && canSubscribe && <p className="text-blue-400 text-xs mt-1">7 días gratis</p>}
                    </div>
                    <ul className="space-y-2 mb-4">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                          <CheckCircle className={`w-3.5 h-3.5 ${plan.popular ? 'text-amber-500' : 'text-green-500'} flex-shrink-0`} />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPayment || isCurrentPlan}
                      className={`w-full ${plan.popular ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-zinc-800 hover:bg-zinc-700 text-white'}`}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {isCurrentPlan ? 'Plan actual' : processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Seleccionar'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-center text-zinc-600 text-xs mt-4">Sin contratos · Cancela cuando quieras</p>
        </>
      )}
    </div>
  );

  if (user) return <SidebarLayout user={user}><SubscriptionContent /></SidebarLayout>;
  return <div className="min-h-screen bg-black"><SubscriptionContent /></div>;
}
